import { Separator } from "@arcadeai/design-system/components/ui/atoms/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@arcadeai/design-system/components/ui/atoms/sidebar";
import { Skeleton } from "@arcadeai/design-system/components/ui/atoms/skeleton";
import { Outlet, useMatch } from "@tanstack/react-router";
import { type JSX, memo, Suspense } from "react";
import { PostHogPageViewTracker } from "@/analytics/posthog-pageview-tracker";
import { InvitationTrigger } from "@/app/invitations/components/shared/invitation-trigger";
import { OrganizationSwitcher } from "@/app/organizations/components/organization-switcher";
import { ProjectSwitcher } from "@/app/projects/components/project-switcher";
import { DashboardCommandDialog } from "@/components/custom/command/dashboard-command-dialog";
import { PylonChat } from "@/components/custom/pylon-chat";
import { AppSidebar } from "@/components/custom/sidebar/app-sidebar";
import { Breadcrumbs } from "@/components/custom/sidebar/breadcrumbs";
import { ModeToggle } from "@/components/custom/theme/mode-toggle";

const LayoutComponent = (): JSX.Element => {
  const isOnboardingRoute = useMatch({
    from: "/_authenticated/orgs/$organizationId/projects/$projectId/onboarding",
    shouldThrow: false,
  });

  if (isOnboardingRoute) {
    return <Outlet />;
  }

  return (
    <div className="flex h-svh flex-col">
      <SidebarProvider
        className="min-h-0 w-svw max-w-full flex-1"
        style={
          // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
          {
            "--sidebar-width": "calc(var(--spacing) * 60)",
            "--header-height": "calc(var(--spacing) * 12)",
          } as React.CSSProperties
        }
      >
        <AppSidebar />
        <PostHogPageViewTracker />
        <PylonChat />
        <SidebarInset className="min-h-0 overflow-hidden">
          <header className="mb-2.5 flex h-(--header-height) shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)">
            <div className="flex h-full w-full items-center justify-between gap-2 px-4">
              <div className="flex h-full items-center gap-2">
                <SidebarTrigger className="-ml-1" />
                <Separator
                  className="self-center! mr-1.5 h-4!"
                  orientation="vertical"
                />
                <div className="flex items-center">
                  <Suspense
                    fallback={<Skeleton className="h-5 w-24 rounded" />}
                  >
                    <OrganizationSwitcher triggerVariant="ghost" />
                  </Suspense>
                  <span className="mr-3 ml-1 text-muted-foreground text-sm">
                    /
                  </span>
                  <Suspense
                    fallback={<Skeleton className="h-5 w-20 rounded" />}
                  >
                    <ProjectSwitcher triggerVariant="ghost" />
                  </Suspense>
                  <span className="mr-3 ml-1 text-muted-foreground text-sm">
                    /
                  </span>
                  <Breadcrumbs />
                </div>
              </div>
              <div className="flex items-center gap-4">
                <DashboardCommandDialog />
                <Suspense>
                  <InvitationTrigger />
                </Suspense>
                <div className="flex items-center gap-4">
                  <ModeToggle />
                </div>
              </div>
            </div>
          </header>
          <div className="scroll-fade min-h-0 min-w-0 flex-1 overflow-y-auto px-6 py-2">
            <Outlet />
          </div>
        </SidebarInset>
      </SidebarProvider>
    </div>
  );
};

export const Layout = memo(LayoutComponent);
